import { RulesReadmodelType } from '@bett3r-dev/teselly-domain';
// Values come from the jsonschema-free `/automation/constants` subpath — importing
// them from the barrel would pull every domain `*.types.ts` (and the CJS-only
// jsonschema-definer) into the Vite browser graph. See constants.ts.
import { HIERARCHY_PRECEDENCE, normalizeOverrideGroup } from '@bett3r-dev/teselly-domain/automation/constants';
import { ScopeAncestor, ScopeNode } from './scopeTypes';

/**
 * Pure inheritance/override/bypass/disabled resolver for the Reglas catalog.
 *
 * THIS MIRRORS THE BACKEND `findRulesToExecute` (rules-utils.ts) EXACTLY.
 * The chosen core invariant (see SDD "The Inheritance Resolver"): the Reglas
 * table must NEVER show a rule as applying/active under a scope where the
 * backend's hierarchy walk would not. The "winning" set produced here
 * (`origin === 'own' | 'inherited'`) MUST equal the union, over every
 * triggering event present among the rules, of the rule ids that
 * `findRulesToExecute` would execute for the same scope and that trigger. The
 * backend runs once per `triggeringEvent` and filters its rule query by it, so an
 * override group only competes among rules sharing a trigger; this resolver
 * therefore walks once per trigger, plus once over trigger-less rules (see
 * `resolveRulesForScope`).
 *
 * Beyond the winners, this resolver also annotates the rules that LOSE
 * (`overridden` / `bypassed` / `disabled`) so the UI can render them with the
 * appropriate Origen/Estado treatment. Bypass rows themselves are NEVER
 * emitted as their own entries — they only feed the suppression calc.
 *
 * The function is pure: no side effects, no backend calls, no React.
 */

// HIERARCHY_PRECEDENCE and normalizeOverrideGroup are imported from
// `@bett3r-dev/teselly-domain` (`rules-hierarchy.ts`) — the single source of truth
// shared with the backend `findRulesToExecute`, so the resolver and the backend walk
// can no longer drift.

// =============================================================================
// TYPES
// =============================================================================

/**
 * Display origin / state for a resolved rule (Origen / Estado columns).
 *
 * - `own` — defined at the selected scope itself (most-specific step).
 * - `inherited` — active, inherited from an ancestor scope.
 * - `overridden` — inherited but beaten by a more-specific rule sharing its
 *   override group (or a more-specific same-definition disabled rule).
 * - `bypassed` — suppressed by a more-specific bypass rule's targets.
 * - `disabled` — `enabled === false`.
 *
 * Winners (the executable set) are exactly `own` + `inherited`.
 */
export type ResolvedRuleOrigin =
  | 'own'
  | 'inherited'
  | 'overridden'
  | 'bypassed'
  | 'disabled';

/**
 * A rule annotated with its display state for the selected scope.
 */
export type ResolvedRule = {
  rule: RulesReadmodelType;
  origin: ResolvedRuleOrigin;
  /**
   * Human-oriented label of where this rule comes from in the walk. Empty for
   * `own`; otherwise the label of the ancestor scope step that contributed it
   * (e.g. the channel/account label). The UI renders "Heredada · {fromLabel}".
   */
  fromLabel: string;
};

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Lookup keyed `${hierarchy}|${hierarchyLevel}|${entityId}` → rules, mirroring
 * the backend `rulesMap[hierarchy][hierarchyLevel][entityId]` shape (a rule with
 * N `hierarchyEntityIds` is registered under each id).
 */
type RulesLookup = Map<string, RulesReadmodelType[]>;

const lookupKey = ( hierarchy: string, level: string, entityId: string ): string =>
  `${hierarchy}|${level}|${entityId}`;

const buildRulesLookup = ( rules: RulesReadmodelType[]): RulesLookup => {
  const lookup: RulesLookup = new Map();
  rules.forEach(( rule ) => {
    ( rule.hierarchyEntityIds || []).forEach(( entityId ) => {
      const key = lookupKey( rule.hierarchy, rule.hierarchyLevel, entityId );
      const bucket = lookup.get( key );
      if ( bucket ) {
        bucket.push( rule );
      } else {
        lookup.set( key, [rule]);
      }
    });
  });
  return lookup;
};

/**
 * Order the node's ancestor path most-specific → least-specific, matching the
 * backend's `Object.entries( path ).sort( by HIERARCHY_PRECEDENCE )`. The
 * resolver receives the path already as an ordered root → self list, so we
 * reverse the precedence sort here for parity (steps with unknown levels sort
 * last, as `indexOf` returns -1 in the backend).
 */
const orderStepsMostSpecificFirst = (
  hierarchy: string,
  ancestors: ScopeAncestor[]
): ScopeAncestor[] => {
  const order = HIERARCHY_PRECEDENCE[hierarchy] || [];
  return [...ancestors].sort(
    ( a, b ) => order.indexOf( a.level ) - order.indexOf( b.level )
  );
};

// =============================================================================
// RESOLVER
// =============================================================================

/**
 * One walk over `rules`, all sharing a single triggering event (or all
 * trigger-less), annotating each candidate with its display state for `scopeNode`.
 * Winners (`own` / `inherited`) === the set one `findRulesToExecute` call would
 * execute for that trigger and scope.
 *
 * Walk (mirrors `findRulesToExecute`):
 *  1. Order the node's path steps most-specific → least-specific.
 *  2. For each step, look up rules registered under `(hierarchy, level, entityId)`.
 *  3. Bypass rules (`kind === 'bypass'`): if enabled, add `targets[]` to the
 *     suppressed set; the bypass row is never emitted.
 *  4. A rule whose id is in the suppressed set is annotated `bypassed`
 *     (evaluated BEFORE override-group/disabled logic).
 *  5. A rule sharing an already-claimed override group, OR a same-definition
 *     rule deeper than an already-disabled one (`${ruleDefinitionId}:${entityId}`),
 *     is annotated `overridden`.
 *  6. A rule with `enabled === false` records its disabled key and is annotated
 *     `disabled`.
 *  7. Otherwise the rule wins: it claims its override groups and is annotated
 *     `own` (most-specific step) or `inherited` (any ancestor step).
 */
const walkOnce = (
  rules: RulesReadmodelType[],
  scopeNode: ScopeNode,
  hierarchies: string[]
): ResolvedRule[] => {
  const lookup = buildRulesLookup( rules );
  const resolved: ResolvedRule[] = [];
  // Guard against the same rule being emitted twice when it would match in both
  // hierarchies for a root node (account rules live under both chains).
  const emittedRuleIds = new Set<string>();

  hierarchies.forEach(( hierarchy ) => {
    const steps = orderStepsMostSpecificFirst( hierarchy, scopeNode.ancestors );
    if ( steps.length === 0 ) return;

    // The most-specific step IS the selected node → its rules are `own`.
    const ownLevel = steps[0].level;
    const ownEntityId = steps[0].entityId;

    const claimedOverrideGroups = new Set<string>();
    const disabledRules: string[] = [];
    // Per-path set of ruleIds suppressed by a more-specific bypass rule. The
    // precedence ordering (most-specific first) gives the cascade for free.
    const bypassedRuleIds = new Set<string>();

    steps.forEach(( step ) => {
      const stepRules = lookup.get( lookupKey( hierarchy, step.level, step.entityId )) || [];
      const isOwnStep = step.level === ownLevel && step.entityId === ownEntityId;

      stepRules.forEach(( rule ) => {
        // Bypass rules contribute targets and are never emitted themselves.
        if ( rule.kind === 'bypass' ) {
          if ( rule.enabled !== false ) {
            ( rule.targets || []).forEach(( id ) => bypassedRuleIds.add( id ));
          }
          return;
        }

        if ( emittedRuleIds.has( rule.ruleId )) return;

        // Suppressed by a more-specific bypass. Evaluated BEFORE override-group
        // + disabled logic so a bypassed rule never claims a group it would win.
        if ( bypassedRuleIds.has( rule.ruleId )) {
          emittedRuleIds.add( rule.ruleId );
          resolved.push({ rule, origin: 'bypassed', fromLabel: scopeNode.label });
          return;
        }

        const ruleGroups = rule.overrideGroups || [];
        const beatenByDisabled = disabledRules.includes(
          `${rule.ruleDefinitionId}:${step.entityId}`
        );
        const beatenByOverrideGroup = ruleGroups.some(( og ) =>
          claimedOverrideGroups.has( normalizeOverrideGroup( og )));
        if ( beatenByDisabled || beatenByOverrideGroup ) {
          emittedRuleIds.add( rule.ruleId );
          resolved.push({
            rule,
            origin: 'overridden',
            fromLabel: isOwnStep ? '' : scopeNode.label
          });
          return;
        }

        if ( rule.enabled === false ) {
          disabledRules.push( `${rule.ruleDefinitionId}:${step.entityId}` );
          emittedRuleIds.add( rule.ruleId );
          resolved.push({
            rule,
            origin: 'disabled',
            fromLabel: isOwnStep ? '' : scopeNode.label
          });
          return;
        }

        // Winner — claims its override groups and joins the executable set.
        ruleGroups.forEach(( og ) =>
          claimedOverrideGroups.add( normalizeOverrideGroup( og )));
        emittedRuleIds.add( rule.ruleId );
        resolved.push({
          rule,
          origin: isOwnStep ? 'own' : 'inherited',
          fromLabel: isOwnStep ? '' : scopeNode.label
        });
      });
    });
  });

  return resolved;
};

const isWinner = ( entry: ResolvedRule ): boolean =>
  entry.origin === 'own' || entry.origin === 'inherited';

/**
 * Resolve every candidate rule for `scopeNode`, annotated with its display
 * state. Winners (`own` / `inherited`) === the union of what `findRulesToExecute`
 * would execute for the same scope, one call per triggering event.
 *
 * The backend is called with one `triggeringEvent` and filters its rule query by
 * it (`triggeringEvents contains`), so an override group claimed by a rule on one
 * trigger never beats a rule on another. This resolver mirrors that:
 *  - `walkOnce` runs once per trigger present among the (non-bypass) rules, over
 *    the rules whose `triggeringEvents` include it, plus once over the rules with
 *    no trigger at all.
 *  - Bypass rows join every walk, so bypass suppression is unchanged.
 *  - Per rule the walks merge as: a winning entry if any walk has one; otherwise
 *    the first `bypassed` / `disabled` entry; `overridden` only when EVERY walk
 *    the rule took part in overrode it. Rows keep their first-appearance order.
 *
 * @param rules - All account rules INCLUDING bypass rows (the `/rules` query
 *   default-excludes `kind: 'bypass'`; the catalog must opt in so suppression
 *   is computed correctly — see SDD "Critical data requirement").
 * @param scopeNode - The selected scope node, carrying its ordered ancestor path.
 */
export const resolveRulesForScope = (
  rules: RulesReadmodelType[],
  scopeNode: ScopeNode
): ResolvedRule[] => {
  // Group / pure-grouping nodes carry no rules.
  if ( scopeNode.kind === 'group' || scopeNode.ancestors.length === 0 ) return [];

  // The account / all root belongs to every hierarchy; a typed leaf to its own.
  // A channel node ADDITIONALLY resolves `order` rules (warehouse selection,
  // logistics provider, invoice): they are channel-scoped but live in the `order`
  // hierarchy, so they must surface alongside the channel's publication rules. They
  // are intentionally NOT walked for publication/item leaves (an order rule is not a
  // per-publication concern), only for the channel node and the account root.
  const baseHierarchies: string[] =
    scopeNode.hierarchy ? [scopeNode.hierarchy] : Object.keys( HIERARCHY_PRECEDENCE );
  const hierarchies: string[] =
    scopeNode.kind === 'channel' && !baseHierarchies.includes( 'order' )
      ? [ ...baseHierarchies, 'order' ]
      : baseHierarchies;

  const isBypass = ( r: RulesReadmodelType ): boolean => r.kind === 'bypass';
  const triggers = new Set<string>();
  let hasTriggerlessRule = false;
  rules.forEach(( r ) => {
    if ( isBypass( r )) return;
    const events = r.triggeringEvents || [];
    if ( events.length === 0 ) hasTriggerlessRule = true;
    events.forEach(( e ) => triggers.add( e ));
  });
  const walks: RulesReadmodelType[][] = [...triggers].map(( trigger ) =>
    rules.filter(( r ) => isBypass( r ) || ( r.triggeringEvents || []).includes( trigger )));
  if ( hasTriggerlessRule ) {
    walks.push( rules.filter(( r ) => isBypass( r ) || ( r.triggeringEvents || []).length === 0 ));
  }

  // Map#set on an existing key keeps its insertion position → first-appearance order.
  const merged = new Map<string, ResolvedRule>();
  walks.forEach(( walkRules ) => {
    walkOnce( walkRules, scopeNode, hierarchies ).forEach(( entry ) => {
      const current = merged.get( entry.rule.ruleId );
      if ( !current
        || ( current.origin === 'overridden' && entry.origin !== 'overridden' )
        || ( !isWinner( current ) && isWinner( entry ))) {
        merged.set( entry.rule.ruleId, entry );
      }
    });
  });
  return [...merged.values()];
};
